import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useApi } from '../../hooks/useApi';
import { useAuthUserContext } from '../../providers'
import { Donor, ApiDonor } from '../../models/Donor';
import { DonorBasicInfo } from '../../models/DonorBasicInfo';
import { DonorPhysicalInfo } from '../../models/DonorPhysicalInfo';
import { DonorRequest } from '../../models/DonorRequest';
import BasicInfo from './BasicInfo';
import Photos from './Photos';
import PhysicalInfo from './PhysicalInfo';
import Request from './Request';
import Spinner from '../../components/Spinner';
import { useTitle } from '../../hooks/useTitle';

const MypageDonorProfile = () => {
  const authUser = useAuthUserContext().user;
  const [ userNo, setUserNo ] = useState<number>(0);
  const [ donorName, setDonorName ] = useState<string>('');
  const [ basicInfo, setBasicInfo ] = useState<DonorBasicInfo | null>(null);
  const [ physicalInfo, setPhysicalInfo ] = useState<DonorPhysicalInfo | null>(null);
  const [ donorRequest, setDonorRequest ] = useState<DonorRequest | null>(null);
  const [ kycCertified, setKycCertified ] = useState<boolean>(false);
  const [ stdCertified, setStdCertified ] = useState<boolean>(false);
  const [ semenCertified, setSemenCertified ] = useState<boolean>(false);
  const [ photoFile, setPhotoFile ] = useState<File | null>(null);
  const [ photoPreview, setPhotoPreview ] = useState<string>('');
  const [ photoStatus, setPhotoStatus ] = useState<string>('');
  const [ photoComments, setPhotoComments ] = useState<string>('');
  const [ removePhoto, setRemovePhoto ] = useState<boolean>(false);
  const { get, post, loading } = useApi();

  useTitle('Ton Bébé | プロフィール');

  useEffect(() => {
    get({
      endpoint: `/api/v1/donors/${ authUser?.userId }`,
      onSuccess: (res) => {
        const apiDonor: ApiDonor = res.data;
        const d = new Donor(apiDonor);
        setUserNo(d.userNo || 0);
        setDonorName(d.name || '');
        setBasicInfo(d.basicInfo);
        setPhysicalInfo(d.physicalInfo);
        setDonorRequest(d.request);
        setKycCertified(d.isKycCertified);
        setStdCertified(d.std.certified);
        setSemenCertified(d.semen.certified);
        setPhotoPreview(d.photoUrl || '');
        setPhotoStatus(d.photoStatus || '');
        setPhotoComments(d.photoComments || '');
      },
      onError: (err) => {
        toast.error("情報の取得に失敗しました。時間をおいてお試しください。");
      },
    });
  }, [ get, authUser?.userId ])

  const validateDonorName = () => {
    if (!donorName) {
      toast.error("イニシャルは必須です。");
      return false;
    }
    if (!/^[A-Z]{2}$/.test(donorName)) {
      toast.error("イニシャルは英字大文字2文字で入力してください。");
      return false;
    }
    return true;
  };

  const handleSaveDraft = () => {
    if (!validateDonorName()) return;
    execSave(false);
  }

  const handleSaveAndPublish = () => {
    if (!validateDonorName()) return;
    execSave(true);
  }

  const execSave = (is_public: boolean) => {
    const fd = new FormData();
    fd.append('is_public', String(is_public));
    fd.append('user_no', String(userNo));
    fd.append('name', donorName);
    fd.append('birth_date', basicInfo?.birthDate || '');
    (basicInfo?.areas || []).forEach((a: string) => fd.append('areas[]', a));
    fd.append('college_type', basicInfo?.collegeType || 'PUBLIC');
    fd.append('major_type', basicInfo?.majorType || '');
    fd.append('college', basicInfo?.college || '');
    fd.append('major', basicInfo?.major || '');
    fd.append('marriage', basicInfo?.marriage || 'UNMARRIED');
    fd.append('son', String(basicInfo?.son ?? ''));
    fd.append('daughter', String(basicInfo?.daughter ?? ''));
    fd.append('comments', basicInfo?.comments || '');
    fd.append('nationality', basicInfo?.nationality || '');
    fd.append('race', basicInfo?.race || '');
    fd.append('blood_type', physicalInfo?.bloodType || 'A');
    fd.append('blood_rh', physicalInfo?.bloodRh || '');
    fd.append('height', String(physicalInfo?.height ?? ''));
    fd.append('weight', String(physicalInfo?.weight ?? ''));
    fd.append('body_figure', physicalInfo?.bodyFigure || 'NORMAL');
    fd.append('hair_type', physicalInfo?.hairType || '');
    fd.append('hair_color', physicalInfo?.hairColor || '');
    fd.append('eyelid', physicalInfo?.eyelid || '');
    (donorRequest?.methods || []).forEach((m: string) => fd.append('methods[]', m));
    fd.append('gratuity', String(donorRequest?.gratuity ?? ''));
    fd.append('travel_expenses', donorRequest?.travelExpenses || '');
    fd.append('identity_disclosure', donorRequest?.identityDisclosure || '');
    fd.append('certificate_presentation', donorRequest?.certificatePresentation || '');
    fd.append('origin_right', donorRequest?.originRight || '');

    if (photoFile) {
      fd.append('photo', photoFile);
    }
    if (removePhoto) {
      fd.append('remove_photo', 'true');
    }

    post({
      endpoint: `/api/v1/donors/${ authUser?.userId }`,
      method: 'PATCH',
      formData: fd,
      onSuccess: () => {
        toast.success(is_public ? "公開しました。" : "保存しました。");
        setBasicInfo(prev => prev ? prev.set("isPublic", is_public) : null);
        setPhotoFile(null);
        setRemovePhoto(false);
      },
      onError: () => {
        toast.error("保存に失敗しました。時間をおいて再度お試しください。");
      },
    });
  };

  return (
    <main className="sub">
      { loading && <Spinner overlay={ true } /> }
      <section className="donor_profile">
        <h2 className="main_title">
          <span>
            プロフィール 現在
            <strong>
              { basicInfo && basicInfo.isPublic ? '公開中' : '未公開' }
            </strong>
            状態
          </span>
          <Link to={ `/donor/${ userNo }` } target="_blank" className="profile_link">見る</Link>
        </h2>

    <div className="form">
        <Photos
          photoPreview={ photoPreview }
          photoStatus={ photoStatus }
          photoComments={ photoComments }
          canUpload={ stdCertified || semenCertified }
          onPhotoChange={ (file, preview) => {
            setPhotoFile(file);
            setPhotoPreview(preview);
            setRemovePhoto(false);
          } }
          onPhotoRemove={ () => {
            setPhotoFile(null);
            setPhotoPreview('');
            setRemovePhoto(true);
          } }
        />
        <BasicInfo
          userNo={ userNo || 0 }
          donorName={ donorName || '' }
          setDonorName={ setDonorName }
          basicInfo={ basicInfo || null}
          setBasicInfo={ setBasicInfo }
          kycCertified={ kycCertified }
        />
        <PhysicalInfo
          physicalInfo={ physicalInfo || null }
          setPhysicalInfo={ setPhysicalInfo }
        />
        <Request
          donorRequest={ donorRequest || null }
          setDonorRequest={ setDonorRequest }
        />
        <ul className="btnarea">
          <li>
            <input
              type="submit"
              value="保存＆公開する"
              className="w_bluebtn"
              onClick={ handleSaveAndPublish}
              disabled={ loading }
            />
          </li>
          <li>
            <input
              type="submit"
              value={ basicInfo && basicInfo.isPublic ? "非公開にする" : "一時保存する" }
              className="w_bluebtn"
              onClick={ handleSaveDraft }
              disabled={ loading }
            />
          </li>
        </ul>
        </div>
      </section>
    </main>
  );
}

export default MypageDonorProfile;
